import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Button, Select, InputNumber, Typography, Alert, DatePicker, Checkbox, Row, Col } from 'antd';
import { CashFlow, Asset, Liability, ParticipantResponse, EstateOwner } from '../../types';
import axios from 'axios';
import dayjs from 'dayjs';
import Cookies from 'js-cookie';
// Assuming services exist based on file structure
import { AssetService } from '../assets/asset.service';
import { LiabilityService } from '../liabilities/liability.service';
import { fetchParticipants } from '../participant/participantService';
import { fetchEstateDetails } from '../estatelist/estateService';
import { MONTH_OPTIONS, RECURRENCE_PRESETS, presetFor } from './recurrence';

const { Title, Text } = Typography;
const { Option } = Select;

interface AddEditCashFlowFormProps {
  open: boolean;
  onClose: () => void;
  initialData?: CashFlow;
  onSubmit: (data: CashFlow) => Promise<void>; // Ensure onSubmit returns a Promise
  estateId: string; // Need estateId to fetch assets/liabilities
}

interface ValidationError {
  property: string;
  constraints: { [key: string]: string };
}

interface ErrorResponse {
  statusCode: number;
  message: ValidationError[];
  error: string;
}

const AddEditCashFlowForm: React.FC<AddEditCashFlowFormProps> = ({
  open,
  onClose,
  initialData,
  onSubmit,
  estateId,
}) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string>('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [liabilities, setLiabilities] = useState<Liability[]>([]);
  const [participants, setParticipants] = useState<ParticipantResponse[]>([]);
  const [estateOwner, setEstateOwner] = useState<EstateOwner | null>(null);

  const isEditing = !!initialData;

  useEffect(() => {
    if (open && estateId) {
      fetchDropdownData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, estateId]);

  const fetchDropdownData = async () => {
    try {
      const token = Cookies.get('accessToken') || '';

      const [assetsRes, liabilitiesRes, participantsRes, estate] = await Promise.all([
        AssetService.fetchAssets({ estateId, page: 1, limit: 1000 }),
        LiabilityService.fetchLiabilities({ estateId, page: 1, limit: 1000 }),
        fetchParticipants(token, estateId),
        fetchEstateDetails(estateId, token)
      ]);

      setAssets(assetsRes.data);
      setLiabilities(liabilitiesRes.data);
      setParticipants(participantsRes);
      setEstateOwner(estate?.estateOwner ?? null);
    } catch (error) {
      console.error('Error fetching dropdown data:', error);
      // Optional: Display a message to the user
      // message.error('Failed to load dropdown data');
    }
  };

  /**
   * The estate owner is a valid payer whether or not they were added to the
   * participants list, so make sure they are always selectable.
   */
  const participantOptions = React.useMemo(() => {
    const options = participants.map((participant) => ({
      uid: participant.uid,
      label: `${participant.firstName ?? ''} ${participant.lastName ?? ''}`.trim() || participant.email,
    }));

    if (estateOwner?.uid && !options.some((option) => option.uid === estateOwner.uid)) {
      options.unshift({
        uid: estateOwner.uid,
        label: `${`${estateOwner.firstName ?? ''} ${estateOwner.lastName ?? ''}`.trim() || estateOwner.email} (estate owner)`,
      });
    }

    return options;
  }, [participants, estateOwner]);

  useEffect(() => {
    if (initialData) {
      form.setFieldsValue({
        name: initialData.name,
        description: initialData.description,
        type: initialData.type,
        // Decimal values arrive as strings from some payloads, and a string
        // fails the numeric rule below, which forced a retype on every edit.
        amount: initialData.amount === undefined || initialData.amount === null
          ? undefined
          : Number(initialData.amount),
        date: initialData.date ? dayjs(initialData.date) : null,
        assetId: initialData.assetId ?? undefined,
        liabilityId: initialData.liabilityId ?? undefined,
        participantId: initialData.participantId ?? undefined,
        accountNo: initialData.accountNo,
        isRecurring: initialData.isRecurring,
        recurrencePreset: presetFor(initialData.recurrenceInterval, initialData.recurrenceCount),
        recurrenceInterval: initialData.recurrenceInterval ?? 'monthly',
        recurrenceCount: initialData.recurrenceCount ?? 1,
        hasSeason: !!initialData.seasonStartMonth && !!initialData.seasonEndMonth,
        seasonStartMonth: initialData.seasonStartMonth ?? undefined,
        seasonEndMonth: initialData.seasonEndMonth ?? undefined,
        nextDueDate: initialData.nextDueDate ? dayjs(initialData.nextDueDate) : null,
      });
    } else {
      form.resetFields();
    }
    setSubmitError(''); // Reset error state when form opens
  }, [initialData, form, open]);

  /**
   * Choosing a preset fixes the interval and count behind it, so the numeric
   * inputs only appear for a custom schedule.
   */
  const handlePresetChange = (preset: string) => {
    const match = RECURRENCE_PRESETS.find((option) => option.value === preset);
    if (match?.interval) {
      form.setFieldsValue({ recurrenceInterval: match.interval, recurrenceCount: match.count });
    }
  };

  /**
   * Money against an estate asset is the owner's unless someone else is named,
   * so fill them in on selection rather than leaving the field blank.
   */
  const handleAssetChange = (assetId?: string) => {
    if (assetId && !form.getFieldValue('participantId') && estateOwner?.uid) {
      form.setFieldsValue({ participantId: estateOwner.uid });
    }
  };

  const handleSubmit = async (values: any) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const seasonal = values.isRecurring && values.hasSeason;

      const data: CashFlow = {
        id: initialData?.id || '', // Backend should handle ID generation; adjust as needed
        estateId: initialData?.estateId || estateId, // Ensure estateId is set appropriately
        name: values.name,
        description: values.description,
        type: values.type,
        amount: values.amount,
        // Send the calendar day. A full timestamp shifts the date whenever the
        // browser and the server sit in different timezones.
        date: values.date ? values.date.format('YYYY-MM-DD') : undefined,
        assetId: values.assetId ?? null,
        liabilityId: values.liabilityId ?? null,
        participantId: values.participantId ?? null,
        accountNo: values.accountNo,
        isRecurring: !!values.isRecurring,
        recurrenceInterval: values.isRecurring ? values.recurrenceInterval : null,
        recurrenceCount: values.isRecurring ? Number(values.recurrenceCount) || 1 : 1,
        seasonStartMonth: seasonal ? values.seasonStartMonth : null,
        seasonEndMonth: seasonal ? values.seasonEndMonth : null,
        nextDueDate: values.isRecurring && values.nextDueDate
          ? values.nextDueDate.format('YYYY-MM-DD')
          : null,
      };
      await onSubmit(data); // Await the onSubmit Promise
      form.resetFields();
      onClose(); // Close the modal only on successful submission
    } catch (error: any) {
      console.error('Error submitting form:', error);

      // Check if the error is an AxiosError with a response
      if (axios.isAxiosError(error) && error.response) {
        const { status, data } = error.response;

        if (status === 400 && Array.isArray(data.message)) {
          const errorResponse = data as ErrorResponse;

          // Iterate through each validation error and set form field errors
          errorResponse.message.forEach((validationError) => {
            const { property, constraints } = validationError;
            const messages = Object.values(constraints).join(', '); // Combine multiple constraints
            form.setFields([
              {
                name: property,
                errors: [messages],
              },
            ]);
          });
        } else {
          // For other status codes or error formats, display a generic error message
          setSubmitError(data.message || 'An unexpected error occurred.');
        }
      } else {
        // For non-Axios errors, display a generic error message
        setSubmitError(error?.message || 'Failed to submit form. Please try again.');
      }

      // The modal remains open, allowing the user to correct any issues
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      title={isEditing ? 'Edit Cash Flow' : 'Add Cash Flow'}
      destroyOnClose
      width={600}
      bodyStyle={{ padding: '24px' }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          type: 'Inbound',
          amount: 0,
          recurrencePreset: 'monthly',
          recurrenceInterval: 'monthly',
          recurrenceCount: 1,
        }}
      >
        {/* Display generic submission error if any */}
        {submitError && (
          <Alert
            message={submitError}
            type="error"
            showIcon
            style={{ marginBottom: '16px' }}
          />
        )}

        {/* CashFlow Identifiers Section */}
        <Title level={5}>CashFlow Identifiers</Title>
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true, message: 'Please enter a name' }]}
        >
          <Input placeholder="Enter cash flow name" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          rules={[{ required: true, message: 'Please enter a description' }]}
        >
          <Input.TextArea rows={3} placeholder="Enter cash flow description" />
        </Form.Item>

        {/* CashFlow Type and Payment Amount Section */}
        <Title level={5}>CashFlow Details</Title>
        <Form.Item
          label="CashFlow Type"
          name="type"
          rules={[{ required: true, message: 'Please select cash flow type' }]}
        >
          <Select placeholder="Select cash flow type">
            <Option value="Inbound">Inbound</Option>
            <Option value="Outbound">Outbound</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="Payment Amount"
          name="amount"
          rules={[
            { required: true, message: 'Please enter the payment amount' },
            { type: 'number', min: 0, message: 'Amount must be a positive number' },
          ]}
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="Payment Amount" />
        </Form.Item>

        <Form.Item
          label="Date"
          name="date"
          rules={[{ required: true, message: 'Please select a date' }]}
        >
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>

        <Title level={5}>Linking</Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item label="Link to Asset" name="assetId">
              <Select allowClear placeholder="Select Asset" onChange={handleAssetChange}>
                {assets.map(asset => (
                  <Option key={asset.uid} value={asset.uid}>{asset.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Link to Liability" name="liabilityId">
              <Select allowClear placeholder="Select Liability">
                {liabilities.map(liability => (
                  <Option key={liability.uid} value={liability.uid}>{liability.name}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              label="Link to Participant"
              name="participantId"
              extra="Defaults to the estate owner when the cash flow is linked to an estate asset."
            >
              <Select allowClear placeholder="Select Participant">
                {participantOptions.map(option => (
                  <Option key={option.uid} value={option.uid}>{option.label}</Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Account Number" name="accountNo">
              <Input placeholder="Enter Account Number" />
            </Form.Item>
          </Col>
        </Row>

        <Title level={5}>Recurrence</Title>
        <Form.Item name="isRecurring" valuePropName="checked">
          <Checkbox>Is Recurring Bill?</Checkbox>
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.isRecurring !== currentValues.isRecurring}
        >
          {({ getFieldValue }) =>
            getFieldValue('isRecurring') ? (
              <>
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      label="Repeats"
                      name="recurrencePreset"
                      rules={[{ required: true, message: 'Please select how often this repeats' }]}
                    >
                      <Select placeholder="Select Interval" onChange={handlePresetChange}>
                        {RECURRENCE_PRESETS.map(preset => (
                          <Option key={preset.value} value={preset.value}>{preset.label}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="Next Due Date"
                      name="nextDueDate"
                      rules={[{ required: true, message: 'Please select next due date' }]}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, current) => prev.recurrencePreset !== current.recurrencePreset}
                >
                  {({ getFieldValue: getValue }) =>
                    getValue('recurrencePreset') === 'custom' ? (
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Every"
                            name="recurrenceCount"
                            rules={[{ required: true, type: 'number', min: 1, max: 365, message: 'Enter 1 or more' }]}
                          >
                            <InputNumber min={1} max={365} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Unit"
                            name="recurrenceInterval"
                            rules={[{ required: true, message: 'Please select a unit' }]}
                          >
                            <Select>
                              <Option value="daily">Days</Option>
                              <Option value="weekly">Weeks</Option>
                              <Option value="monthly">Months</Option>
                              <Option value="yearly">Years</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : (
                      // Keep the resolved interval and count in the payload
                      // even while the custom inputs are hidden.
                      <>
                        <Form.Item name="recurrenceInterval" hidden><Input /></Form.Item>
                        <Form.Item name="recurrenceCount" hidden><Input /></Form.Item>
                      </>
                    )
                  }
                </Form.Item>

                <Form.Item name="hasSeason" valuePropName="checked" style={{ marginBottom: 8 }}>
                  <Checkbox>Only active during part of the year</Checkbox>
                </Form.Item>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  For seasonal costs such as lawn care (June to October) or heating
                  (November to March). Outside the season the bill is skipped and
                  picks up again at the start month.
                </Text>

                <Form.Item
                  noStyle
                  shouldUpdate={(prev, current) => prev.hasSeason !== current.hasSeason}
                >
                  {({ getFieldValue: getValue }) =>
                    getValue('hasSeason') ? (
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            label="Season starts"
                            name="seasonStartMonth"
                            rules={[{ required: true, message: 'Please select a start month' }]}
                          >
                            <Select placeholder="Start Month">
                              {MONTH_OPTIONS.map(month => (
                                <Option key={month.value} value={month.value}>{month.label}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label="Season ends"
                            name="seasonEndMonth"
                            rules={[{ required: true, message: 'Please select an end month' }]}
                          >
                            <Select placeholder="End Month">
                              {MONTH_OPTIONS.map(month => (
                                <Option key={month.value} value={month.value}>{month.label}</Option>
                              ))}
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>
                    ) : null
                  }
                </Form.Item>
              </>
            ) : null
          }
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting} style={{ marginRight: '8px' }}>
            {isEditing ? 'Update Cash Flow' : 'Add Cash Flow'}
          </Button>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditCashFlowForm;
